import TeambookIconButton from "../../../../default_components/TeambookIconButton";
import { icons } from "../../../../default_images/IconsList";
import {
  useBookingModalStore,
  useBookingsCreatingStore,
  useBookingsEditingStore,
  usePlannerStore,
  useSelectedProjectStore,
} from "../../../../../stores/planner";
import { useTranslation } from "react-i18next";
import { shallow } from "zustand/shallow";
import { useProfileStore } from "../../../../../stores/profileStore";
import TeambookDialogDangerAction from "../../../../default_components/TeambookDialogDangerAction";
import { useState } from "react";
import { parseStringTimeIntoInteger } from "./StartTimeBlock";
import { ToDateString } from "../../../../../lib/DateUtils";
import { DateTime } from "luxon";
import { useAccountStore } from "../../../../../stores/accountStore";
import lsKeys from "../../../../default_values/defaultKeys";
import { isWeekend } from "date-fns";
import Api from "../../../../../Api";
import TeambookDialog from "../../../../default_components/TeambookDialog";
import TeambookBlueButton from "../../../../default_components/TeambookBlueButton";
import TeambookOutlinedButton from "../../../../default_components/TeambookOutlinedButton";
import PropTypes from "prop-types";

const ActionButtonsRow = ({
  modalType,
  closeModal,
  hideTimeOffOptions,
  createBookingsRequest,
  tentative,
  startTimeWindowOpened,
  startTime,
  editorRef,
  usesBudget,
  task,
  deleteBookingsAfterSave,
  updateBookingsRequest,
  usersToCreateBookings,
  orderedPlannerUsers,
  createBookingsRef,
  location,
  deleteBookingsRequest,
}) => {
  const { t } = useTranslation();
  const role = localStorage.getItem("tb-role") || "regular";

  const [currentUser] = useProfileStore((state) => [state.profile], shallow);
  const [precheckResult] = usePlannerStore((state) => [state.precheckResult], shallow);
  const [account] = useAccountStore((state) => [state.account], shallow);
  const [selectedProject, selectedTimeOff, projectsType] = useSelectedProjectStore(
    (state) => [state.selectedProject, state.selectedTimeOff, state.projectsType],
    shallow
  );
  const [bookingsDuration, bookingsCreating] = useBookingsCreatingStore(
    (state) => [state.bookingsDuration, state.bookingsCreating],
    shallow
  );
  const [editingBookings, editingDuration] = useBookingsEditingStore(
    (state) => [state.bookingsEditing, state.editingDuration],
    shallow
  );
  const [repetition] = useBookingModalStore((state) => [state.repetition], shallow);

  const [deletionOpen, setDeletionOpen] = useState(false);
  const [editMultipleWindowOpened, setEditMultipleWindowOpened] = useState(false);
  const [saveButtonClicked, setSaveButtonClicked] = useState(false);

  const isEditingBlocked = () => {
    if (editingBookings.some((b) => ["google_calendar", "outlook_calendar"].includes(b.created_with))) {
      return true;
    }

    switch (role) {
      case "regular":
      case "contractor":
        return true;
      case "self_planner":
        return editingBookings.some(({ user_id }) => user_id !== currentUser.id);
      default:
        return false;
    }
  };

  const isEditButtonDisabled = () => {
    const precheckBlocksCreation = precheckResult?.status === "error";
    return hideTimeOffOptions || precheckBlocksCreation;
  };

  const deleteBookings = () => {
    deleteBookingsRequest(editingBookings);
    setEditMultipleWindowOpened(false);
  };

  const clickOnDeleteBooking = () => {
    if (useBookingsEditingStore.getState().bookingsEditing.length > 1) {
      setDeletionOpen(true);
    } else {
      deleteBookings();
    }
  };

  const calculateIntegerStartTime = (time) => {
    return parseStringTimeIntoInteger(time);
  };

  const isCreateButtonDisabled = () => {
    const precheckBlocksCreation = precheckResult?.status === "error";
    return saveButtonClicked || hideTimeOffOptions || precheckBlocksCreation;
  };

  const createBookingsClick = () => {
    const weekendsHidden = localStorage.getItem(lsKeys.WEEKENDS_HIDDEN_KEY) === "true";
    const weekendCreating = bookingsCreating.length === 1 && weekendsHidden && isWeekend(bookingsCreating[0][1]);

    if (saveButtonClicked || ["contractor", "regular"].includes(role)) {
      return;
    }

    if (account.subscription.subscription_id === "Free" && account.subscription.projects_available === 10) {
      closeModal();
    }

    setSaveButtonClicked(true);

    createBookingsRequest(
      projectsType === "default" ? selectedProject : selectedTimeOff,
      bookingsDuration,
      tentative,
      startTimeWindowOpened ? calculateIntegerStartTime(startTime) : null,
      editorRef.current.getComment() === "<p></p>" ? "" : editorRef.current.getComment(),
      location,
      {
        repetitionType: repetition.type,
        repetitionEvery: repetition.every.value || 1,
        repetitionEndsAt: repetition.endsAt,
        repetitionEndsAtDate: repetition.endsAtDate,
        repetitionEndsAfter: repetition.endsAfter,
        repetitionEndsAfterNumber: repetition.endsAfterNumber,
        ignoreWeekends: repetition.ignoreWeekends,
      },
      usesBudget,
      task,
      weekendCreating
    );

    closeModal();
  };

  const updateBookings = async () => {
    if (account.subscription.subscription_id === "Free" && account.subscription.projects_available === 10) {
      setEditMultipleWindowOpened(false);
    }

    if (deleteBookingsAfterSave) {
      deleteBookingsRequest(editingBookings);
    } else {
      updateBookingsRequest(
        editingDuration,
        tentative,
        location,
        startTimeWindowOpened ? parseStringTimeIntoInteger(startTime) : null,
        editorRef.current.getComment() === "<p></p>" ? "" : editorRef.current.getComment(),
        usesBudget,
        null,
        null,
        task,
        {
          on: !!repetition.type,
          type: repetition.type,
          every: repetition.every.value || 1,
          ends_on: repetition.endsAt ? ToDateString(repetition.endsAtDate) : null,
          ends_after: repetition.endsAfter ? repetition.endsAfterNumber : null,
          ignore_weekends: repetition.ignoreWeekends,
        }
      );

      setEditMultipleWindowOpened(false);

      editingBookings.forEach(({ id, project_id }) => {
        let editBooking;

        if (document.querySelector(`#booking-${id}`)) {
          editBooking = document.querySelector(`#booking-${id}`);

          let selectedObject = editBooking.querySelector(".booking__project-duration");

          if (selectedObject) {
            selectedObject.innerHTML = editingDuration + "h";
          }
        } else if (document.querySelector(`#booking-${id}-${project_id}`)) {
          editBooking = Array.from(document.querySelectorAll(`.booking-${id}-${project_id}`));

          editBooking.forEach((el) => {
            if (el.querySelector(".booking__project-duration")) {
              el.querySelector(".booking__project-duration").innerHTML = editingDuration + "h";
            }
          });
        }
      });
    }

    if (usersToCreateBookings.length > 0) {
      const project =
        useSelectedProjectStore.getState().projectsType === "time_off"
          ? useSelectedProjectStore.getState().selectedTimeOff
          : useSelectedProjectStore.getState().selectedProject;

      await new Promise((resolve) => {
        const newBookings = usersToCreateBookings
          .map((user) => {
            const userIndex = orderedPlannerUsers.findIndex((u) => u.id === user.value);

            const userBookings = editingBookings.map((booking) => {
              return [userIndex, DateTime.fromISO(booking.date).toJSDate()];
            });

            createBookingsRef.current[userIndex]?.updateBookings(userBookings);

            return userBookings;
          })
          .flat();

        useBookingsCreatingStore.getState().setBookings(newBookings);

        resolve();
      }).then(() => {
        createBookingsRequest(
          project,
          editingDuration,
          tentative,
          startTimeWindowOpened ? parseStringTimeIntoInteger(startTime) : null,
          editorRef.current.getComment() === "<p></p>" ? "" : editorRef.current.getComment(),
          location,
          {
            repetitionType: repetition.type,
            repetitionEvery: repetition.every.value || 1,
            repetitionEndsAt: repetition.endsAt ? ToDateString(repetition.endsAtDate) : null,
            repetitionEndsAtDate: repetition.endsAtDate,
            repetitionEndsAfter: repetition.endsAfter,
            repetitionEndsAfterNumber: repetition.endsAfterNumber,
            ignoreWeekends: repetition.ignoreWeekends,
          },
          usesBudget,
          task
        );
      });
    }
  };

  return (
    <>
      {modalType === "editing" ? (
        <div className="booking-window__control-buttons__button-row">
          {!isEditingBlocked() && (
            <TeambookIconButton name={icons.TRASH} tooltipTitle={t("delete")} onClick={clickOnDeleteBooking} />
          )}

          <TeambookOutlinedButton onClick={closeModal} text={t("cancel")} style={{ width: "100%" }} />

          {!isEditingBlocked() && (
            <TeambookBlueButton
              disabled={isEditButtonDisabled()}
              id={"saveEditingBooking"}
              onClick={() => {
                if (useBookingsEditingStore.getState().bookingsEditing.length > 1) {
                  setEditMultipleWindowOpened(true);
                } else {
                  updateBookings();
                }
              }}
              text={t("save")}
              variant="contained"
              style={{ width: "100%" }}
            />
          )}
        </div>
      ) : (
        <div className="booking-window__control-buttons__button-row">
          <TeambookOutlinedButton
            text={t("cancel")}
            style={{ width: "100%" }}
            onClick={() => {
              closeModal();
            }}
          />

          <TeambookBlueButton
            onClick={createBookingsClick}
            variant="contained"
            disabled={isCreateButtonDisabled()}
            text={t("create")}
            style={{ width: "100%" }}
          />
        </div>
      )}

      <TeambookDialogDangerAction
        open={deletionOpen}
        setOpen={() => setDeletionOpen(false)}
        contentText={`${t("planning.deleting_multiple_bookings_1")} ${editingBookings.length} ${t(
          "planning.deleting_multiple_bookings_2"
        )}`}
        actionRequest={() => deleteBookingsRequest(editingBookings)}
      />

      <TeambookDialog
        open={editMultipleWindowOpened}
        onClose={() => setEditMultipleWindowOpened(false)}
        title={" "}
        withCloseButton
        content={
          <p style={{ marginBottom: 0 }}>
            {t("planning.editing_multiple_bookings_1")}
            {editingBookings.length}
            {t("planning.editing_multiple_bookings_2")}
          </p>
        }
        actionButtons={[
          {
            text: t("no"),
            onClick: () => {
              Api.Administrators.post_some_statistics("booking_edit_question/no");
              setEditMultipleWindowOpened(false);
            },
            type: "outlined",
          },
          {
            text: t("yes"),
            onClick: () => {
              Api.Administrators.post_some_statistics("booking_edit_question/yes");
              updateBookings();
            },
          },
        ]}
      />
    </>
  );
};

ActionButtonsRow.propTypes = {
  modalType: PropTypes.string.isRequired,
  closeModal: PropTypes.func.isRequired,
  hideTimeOffOptions: PropTypes.bool,
  createBookingsRequest: PropTypes.func.isRequired,
  tentative: PropTypes.bool,
  startTimeWindowOpened: PropTypes.bool,
  startTime: PropTypes.string,
  editorRef: PropTypes.object.isRequired,
  usesBudget: PropTypes.bool,
  task: PropTypes.object,
  deleteBookingsAfterSave: PropTypes.bool,
  updateBookingsRequest: PropTypes.func.isRequired,
  usersToCreateBookings: PropTypes.array.isRequired,
  orderedPlannerUsers: PropTypes.array.isRequired,
  createBookingsRef: PropTypes.object.isRequired,
  location: PropTypes.string,
  deleteBookingsRequest: PropTypes.func.isRequired,
};

export default ActionButtonsRow;
